// client/src/components/layout/TopBar.js
import React, { Fragment, useMemo, useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/auth/AuthProvider';
import { useAuthMutations } from '../../hooks/useAuth';
import { LogOut, UserCircle, Briefcase, Menu, Clock, Zap, Star, ShieldAlert } from 'lucide-react';

const TopBar = ({ toggleSidebar }) => {
    const { isAuthenticated, user, logout, isLoading, isImpersonating, originalAdminName, impersonationExpiresAt } = useAuth();
    const { endImpersonation } = useAuthMutations();
    const navigate = useNavigate();
    const onLogout = () => {
        if (logout) logout();
        navigate('/login');
    };

    // ✅ Contagem regressiva simples até a expiração da concessão — só pra
    // exibição no banner, a validade real é sempre revalidada no backend
    // (middlewares/auth.js::protect) a cada requisição.
    const [tempoRestante, setTempoRestante] = useState('');
    useEffect(() => {
        if (!isImpersonating || !impersonationExpiresAt) return undefined;
        const atualizar = () => {
            const diffMs = new Date(impersonationExpiresAt).getTime() - Date.now();
            if (diffMs <= 0) {
                setTempoRestante('expirando...');
                return;
            }
            const minutos = Math.floor(diffMs / 60000);
            const horas = Math.floor(minutos / 60);
            setTempoRestante(horas > 0 ? `${horas}h ${minutos % 60}min` : `${minutos}min`);
        };
        atualizar();
        const interval = setInterval(atualizar, 30000);
        return () => clearInterval(interval);
    }, [isImpersonating, impersonationExpiresAt]);

    const planInfo = useMemo(() => {
        if (!user) return null;

        const entity = user.account || user.franquia;
        if (!entity) return null;
        
        // Verificar status do plano
        if (entity.status === 'trialing' && entity.trialExpiresAt) {
            const hoje = new Date();
            const dataExpiracao = new Date(entity.trialExpiresAt);
            const diffTime = dataExpiracao - hoje;
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

            if (diffDays < 0) {
                return { text: 'Seu teste expirou!', isTrial: true, isExpired: true };
            }
            return {
                text: `Teste: ${diffDays} ${diffDays === 1 ? 'dia restante' : 'dias restantes'}`,
                isTrial: true,
                isExpired: false
            };
        }

        if (entity.status === 'active' && entity.plano) {
            return { text: `Plano: ${entity.plano.name}`, isTrial: false };
        }

        return null;
    }, [user]);

    if (isLoading) {
        return (
            <header className="top-bar">
                <div className="flex items-center justify-center w-full h-16 bg-white shadow">
                    Carregando...
                </div>
            </header>
        );
    }

    return (
        <Fragment>
            {isAuthenticated && isImpersonating && (
                <div
                    role="alert"
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.5rem',
                        flexWrap: 'wrap',
                        width: '100%',
                        padding: '0.4rem 1rem',
                        background: '#E53E3E',
                        color: '#fff',
                        fontSize: '0.85rem',
                        fontWeight: 500
                    }}
                >
                    <ShieldAlert size={16} />
                    <span>
                        Você está acessando como <strong>{user?.name}</strong> com acesso de suporte
                        {originalAdminName ? ` concedido a ${originalAdminName}` : ''}
                        {tempoRestante ? ` — expira em ${tempoRestante}` : ''}.
                    </span>
                    <button
                        type="button"
                        onClick={() => endImpersonation()}
                        style={{
                            background: '#fff',
                            color: '#E53E3E',
                            border: 'none',
                            borderRadius: '6px',
                            padding: '0.2rem 0.6rem',
                            fontWeight: 600,
                            fontSize: '0.8rem',
                            cursor: 'pointer'
                        }}
                    >
                        Encerrar acesso
                    </button>
                </div>
            )}
            <header className="top-bar">
                <button
                    onClick={toggleSidebar}
                    className="mobile-menu-button"
                    aria-label="Abrir menu"
                >
                    <Menu size={24} />
                </button>

                <div style={{ flexGrow: 1 }}></div>

                <div className="top-bar-content">
                    {isAuthenticated && user && (
                        <>
                            {planInfo && (
                                <div className={`trial-banner ${planInfo.isExpired ? 'trial-expired' : ''}`}>
                                    {planInfo.isTrial ? <Clock size={16} className="icon" /> : <Star size={16} className="icon" />}
                                    <span>{planInfo.text}</span>
                                    {planInfo.isTrial && !planInfo.isExpired && (
                                        <Link to="/assinatura" className="upgrade-button">
                                            <Zap size={14} /> Fazer Upgrade
                                        </Link>
                                    )}
                                </div>
                            )}

                            <div className="user-info-item sm-visible" title={`Conta: ${user.account?.name || user.franquia?.nome}`}>
                                <Briefcase size={16} className="icon" />
                                <span>{user.account?.name || user.franquia?.nome}</span>
                            </div>
                            <div className="user-info-item">
                                <UserCircle size={20} className="icon" />
                                <span className="hidden sm:inline">Olá, {user.name}</span>
                            </div>
                            <button onClick={onLogout} className="logout-button">
                                <LogOut size={16} className="icon" />
                                <span className="hidden sm:inline">Sair</span>
                            </button>
                        </>
                    )}
                </div>
            </header>
        </Fragment>
    );
};

export default TopBar;